import { create } from "zustand";
import { persist } from "zustand/middleware";
import { FORM_FIELDS } from "../constants";
import type { RequestDepartureFormData } from "../schema/requestDepartureFormSchema";

export const PERSIST_KEY = "request-departure-form";

interface RequestDepartureFormMethods {
  setField(
    key: keyof RequestDepartureFormData | string,
    val: RequestDepartureFormData[keyof RequestDepartureFormData] | unknown,
  ): void;
  setAll(data: Partial<RequestDepartureFormData>): void;
  resetForm(): void;
}

type RequestDepartureFormState = RequestDepartureFormData & RequestDepartureFormMethods;

const isNumericPathSegment = (segment: string) => /^\d+$/.test(segment);

const setValueAtPath = (
  source: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> => {
  const segments = path.split(".");
  const next = { ...source };
  let cursor: Record<string, unknown> | unknown[] = next;

  segments.forEach((segment, index) => {
    const isLast = index === segments.length - 1;
    const key: string | number = isNumericPathSegment(segment) ? Number(segment) : segment;

    if (isLast) {
      (cursor as any)[key] = value;
      return;
    }

    const nextSegment = segments[index + 1] ?? "";
    const nextShouldBeArray = isNumericPathSegment(nextSegment);
    const currentChild = (cursor as any)[key];

    const clonedChild = Array.isArray(currentChild)
      ? [...currentChild]
      : currentChild && typeof currentChild === "object"
        ? { ...currentChild }
        : nextShouldBeArray
          ? []
          : {};

    (cursor as any)[key] = clonedChild;
    cursor = clonedChild;
  });

  return next;
};

export const REQUEST_DEPARTURE_DEFAULT_VALUES: Omit<
  RequestDepartureFormData,
  keyof RequestDepartureFormMethods
> = {
  [FORM_FIELDS.FIRST_VISIT]: true,
  [FORM_FIELDS.MASTER_NAME]: "",
  [FORM_FIELDS.NEXT_PORT]: "",
  [FORM_FIELDS.VESSEL_CONDITION]: "",
  [FORM_FIELDS.REQUEST_ANCHORAGE_CLEARANCE]: false,
  [FORM_FIELDS.PRE_SAILING_MASTER_DECLARATION]: [],
  [FORM_FIELDS.DEPARTURE_CREW_LIST]: [],
  [FORM_FIELDS.CARGO_MANIFEST]: [],
  [FORM_FIELDS.CUSTOMS_CLEARANCE]: undefined,
  [FORM_FIELDS.CUSTOMS_CLEARANCE_UPLOAD_HAPPENED]: false,
  [FORM_FIELDS.REMARKS]: "",
  [FORM_FIELDS.ADDITIONAL_DOCUMENTS]: [],
  [FORM_FIELDS.DRAFT_FORE]: undefined,
  [FORM_FIELDS.DRAFT_AFT]: undefined,
  [FORM_FIELDS.FINAL_DESTINATION]: "",
};

export const useRequestDepartureFormStore = create<RequestDepartureFormState>()(
  persist(
    (set) => ({
      ...(REQUEST_DEPARTURE_DEFAULT_VALUES as RequestDepartureFormState),

      setField: (key, val) =>
        set((state) => {
          if (typeof key === "string" && key.includes(".")) {
            const nextState = setValueAtPath(state as unknown as Record<string, unknown>, key, val);
            return nextState as unknown as RequestDepartureFormState;
          }

          return { ...state, [key]: val } as RequestDepartureFormState;
        }),
      setAll: (data) => set((s) => ({ ...s, ...data })),
      resetForm: () =>
        set((state) => {
          const stateWithoutDynamicKeys = Object.fromEntries(
            Object.entries(state).filter(([fieldName]) => !fieldName.includes(".")),
          );

          return {
            ...stateWithoutDynamicKeys,
            ...REQUEST_DEPARTURE_DEFAULT_VALUES,
          } as RequestDepartureFormState;
        }, true),
    }),
    { name: PERSIST_KEY },
  ),
);
